import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, ExchangePolicyRow, RefundPolicyRow } from '../../api';
import { eventEndpoints, exchangePolicyEndpoints } from '../../api';
import { Badge, Button, EmptyRow, Field, Panel, Table } from '../../ui';
import type { PanelProps } from '../Event';
import { INPUT, dollars, toCents } from './shared';
import { dateWords } from '../../terms';

/**
 * The event's exchange policy (D-66): whether customers exchange online,
 * how long before showtime that closes, the fee per ticket exchanged, and
 * what happens to a downgrade's difference. Policies are a small shared
 * catalog; an event with none keeps the defaults (online until the
 * performance starts, no fee, downgrades refunded). Staff at the window
 * and in Admin exchange past the cutoff, and a manager can waive the fee.
 */
/**
 * Flexible tickets (D-190): whether this event sells the right to hand tickets
 * back, how long that lasts and what it costs.
 *
 * An event that names no policy is not offering them, which is the safe
 * default — this is turned on deliberately, never left on by forgetting to
 * turn it off. The window and the rate are two halves of one offer, so they
 * live on one row and are chosen together.
 */
function FlexiblePanel({ detail, refresh, onError, can }: PanelProps) {
  const eventId = detail.event.id;
  const editable = detail.event.status !== 'ARCHIVED' && can('events.manage');
  const policies = useQuery<{ policies: RefundPolicyRow[] }, ApiError>({ queryKey: ['refund-policies'], queryFn: eventEndpoints.refundPolicies });
  const assign = useMutation({
    mutationFn: (policyId: string | null) => eventEndpoints.update(eventId, { refund_policy_id: policyId }),
    onSuccess: refresh,
    onError: (cause: ApiError) => onError(cause),
  });
  const current = detail.refund_policy ?? null;
  const hours = (minutes: number | null) => (minutes == null ? 'until showtime' : minutes % 60 === 0 ? `${minutes / 60} h before` : `${minutes} min before`);

  return (
    <Panel
      title="Flexible tickets"
      description="A paid option at checkout: the customer pays a share of the order and may hand any of those tickets back, for any reason, until the cutoff, and gets the full ticket price and fees back as credit good for tickets or at the bar. The charge itself is never returned. Nothing is reviewed — they paid for the right, so exercising it happens at once."
    >
      <div className="px-5 py-4 text-sm">
        {current && current.customer_self_service === 1 && current.flexible_basis_points > 0 ? (
          <p>
            <span className="font-semibold text-ink-900">{current.name}</span> <span className="font-mono text-xs text-ink-400">{current.code}</span> · {current.flexible_basis_points / 100}% of the order · {hours(current.self_service_cutoff_minutes_before_start)}
            <span className="mt-1 block text-ink-600">{current.public_text}</span>
          </p>
        ) : (
          <p className="text-ink-600">Not offered on this event. Tickets are refunded by the box office, as they always have been.</p>
        )}
        {editable ? (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <select value={current?.id ?? ''} onChange={(e) => assign.mutate(e.target.value || null)} className={INPUT} aria-label="Flexible ticket policy" disabled={assign.isPending}>
              <option value="">Not offered</option>
              {(policies.data?.policies ?? []).filter((p) => p.active === 1 || p.id === current?.id).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} — {p.flexible_basis_points / 100}%, {hours(p.self_service_cutoff_minutes_before_start)}
                </option>
              ))}
            </select>
            {assign.isPending ? <span className="text-ink-400">Saving…</span> : null}
          </div>
        ) : null}
        <p className="mt-3 text-xs text-ink-400">
          Orders already carrying the charge keep what they bought: changing this changes what is offered from now on, not what was sold.
        </p>
      </div>
    </Panel>
  );
}

export function ExchangesPanel({ detail, refresh, onError, can }: PanelProps) {
  const queryClient = useQueryClient();
  const eventId = detail.event.id;
  const editable = detail.event.status !== 'ARCHIVED' && can('events.manage');
  const policies = useQuery<{ policies: ExchangePolicyRow[] }, ApiError>({ queryKey: ['exchange-policies'], queryFn: exchangePolicyEndpoints.list });
  const [creating, setCreating] = useState(false);
  // Box office only until somebody ticks the box (D-147). A self-service
  // exchange puts a seat back and takes another with nobody looking at it, so
  // it is turned on for an event rather than remembered to be turned off.
  const [form, setForm] = useState({ code: '', name: '', public_text: '', customer_self_service: false, cutoff_hours: '2', fee: '0.00', downgrade_disposition: 'REFUND' as 'REFUND' | 'FORFEIT' });
  const assign = useMutation({
    mutationFn: (policyId: string | null) => eventEndpoints.update(eventId, { exchange_policy_id: policyId }),
    onSuccess: refresh,
    onError: (cause: ApiError) => onError(cause),
  });
  const create = useMutation({
    mutationFn: () => {
      const fee = toCents(form.fee) ?? 0;
      const hours = Number(form.cutoff_hours);
      if (!Number.isFinite(hours) || hours < 0) throw new Error('Cutoff hours must be a number');
      return exchangePolicyEndpoints.create({ code: form.code.trim(), name: form.name.trim(), public_text: form.public_text.trim(), customer_self_service: form.customer_self_service, self_service_cutoff_minutes_before_start: Math.round(hours * 60), fee_cents: fee, downgrade_disposition: form.downgrade_disposition });
    },
    onSuccess: (r) => {
      setCreating(false);
      void queryClient.invalidateQueries({ queryKey: ['exchange-policies'] });
      assign.mutate(r.policy.id);
    },
    onError: (cause: Error) => onError(cause as ApiError),
  });
  const current = detail.exchange_policy;
  return (
    <div className="space-y-4">
      <FlexiblePanel detail={detail} refresh={refresh} onError={onError} can={can} />
      <Panel
        title="Exchange policy"
        description="What this event allows when tickets are exchanged. Customers exchange from their order page within the policy; staff exchange at the window or from Admin → Orders regardless of the cutoff, and a manager can waive the fee or a difference. The fee posts as an Exchange Fee line on the order."
        actions={editable && !creating ? <Button onClick={() => setCreating(true)}>New policy</Button> : null}
      >
        <div className="px-5 py-4 text-sm">
          {current ? (
            <p>
              <span className="font-semibold text-ink-900">{current.name}</span> <span className="font-mono text-xs text-ink-400">{current.code}</span> · {current.customer_self_service ? `online until ${current.self_service_cutoff_minutes_before_start >= 60 ? `${current.self_service_cutoff_minutes_before_start / 60} h` : `${current.self_service_cutoff_minutes_before_start} min`} before showtime` : 'box office only'} · fee {dollars(current.fee_cents)} per ticket · downgrades {current.downgrade_disposition === 'REFUND' ? 'refunded' : 'forfeited'}
              <span className="mt-1 block text-ink-600">{current.public_text}</span>
            </p>
          ) : (
            <p className="text-ink-600">No policy: customers may exchange online until the {dateWords().one} starts, there is no fee, and a downgrade's difference is refunded.</p>
          )}
          {editable ? (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <select value={current?.id ?? ''} onChange={(e) => assign.mutate(e.target.value || null)} className={INPUT} aria-label="Exchange policy" disabled={assign.isPending}>
                <option value="">Defaults (no policy)</option>
                {(policies.data?.policies ?? []).filter((p) => p.active === 1 || p.id === current?.id).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.customer_self_service ? 'online' : 'box office only'}, {dollars(p.fee_cents)}, downgrades {p.downgrade_disposition.toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>
        {creating ? (
          <form
            className="grid gap-3 border-t border-line px-5 py-4 md:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <Field label="Code" htmlFor="xp-code">
              <input id="xp-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={INPUT} required />
            </Field>
            <Field label="Name" htmlFor="xp-name">
              <input id="xp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={INPUT} required />
            </Field>
            <div className="md:col-span-2">
              <Field label="What customers are told" htmlFor="xp-text">
                <textarea id="xp-text" value={form.public_text} onChange={(e) => setForm({ ...form, public_text: e.target.value })} className={`${INPUT} w-full`} rows={2} required />
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.customer_self_service} onChange={(e) => setForm({ ...form, customer_self_service: e.target.checked })} /> Customers may exchange online
              <span className="text-xs text-ink-500">(off: box office only, and the website offers nothing)</span>
            </label>
            <Field label="Online cutoff (hours before showtime)" htmlFor="xp-cutoff">
              <input id="xp-cutoff" type="number" min={0} step="0.5" value={form.cutoff_hours} onChange={(e) => setForm({ ...form, cutoff_hours: e.target.value })} className={INPUT} />
            </Field>
            <Field label="Fee per ticket ($)" htmlFor="xp-fee">
              <input id="xp-fee" type="number" min={0} step="0.01" value={form.fee} onChange={(e) => setForm({ ...form, fee: e.target.value })} className={INPUT} />
            </Field>
            <Field label="Downgrade difference" htmlFor="xp-down">
              <select id="xp-down" value={form.downgrade_disposition} onChange={(e) => setForm({ ...form, downgrade_disposition: e.target.value as 'REFUND' | 'FORFEIT' })} className={INPUT}>
                <option value="REFUND">Refunded to the original tender</option>
                <option value="FORFEIT">Forfeited (no refund)</option>
              </select>
            </Field>
            <div className="flex gap-2 md:col-span-2">
              <Button type="submit" variant="primary" disabled={create.isPending}>
                Create and use
              </Button>
              <Button type="button" onClick={() => setCreating(false)}>
                Cancel
              </Button>
            </div>
          </form>
        ) : null}
      </Panel>
      <Panel title="All policies">
        <Table headers={['Policy', 'Online', 'Cutoff', 'Fee', 'Downgrade', 'Status']}>
          {(policies.data?.policies ?? []).length === 0 ? (
            <EmptyRow columns={6}>No policies yet.</EmptyRow>
          ) : (
            policies.data!.policies.map((p) => (
              <tr key={p.id} className="border-b border-line last:border-0">
                <td className="px-5 py-3 text-sm">
                  <div className="font-medium text-ink-900">{p.name}</div>
                  <div className="text-xs text-ink-400">{p.public_text}</div>
                </td>
                <td className="px-5 py-3 text-sm">{p.customer_self_service ? 'yes' : 'no'}</td>
                <td className="px-5 py-3 text-sm">{p.self_service_cutoff_minutes_before_start >= 60 ? `${p.self_service_cutoff_minutes_before_start / 60} h` : `${p.self_service_cutoff_minutes_before_start} min`}</td>
                <td className="tabular px-5 py-3 text-sm">{dollars(p.fee_cents)}</td>
                <td className="px-5 py-3 text-sm">{p.downgrade_disposition.toLowerCase()}</td>
                <td className="px-5 py-3">
                  <Badge tone={p.active ? 'ok' : 'neutral'}>{p.active ? 'active' : 'retired'}</Badge>
                </td>
              </tr>
            ))
          )}
        </Table>
      </Panel>
    </div>
  );
}
